import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  ErrorEventHist,
  EventPage,
  HealthEventHist,
  HistoryBucket,
  SignalHistory,
  StateEventHist,
} from './history.types';

// Site-scoped `/v1/history/*` — cloud prefixes `/sites/{id}` via resolvePath (D-040).
// These are historical snapshots (not live), so no polling by default; the range
// is the cache key. Disabled until a MAC + range is present.
const BASE = '/v1/history/devices';
const KEY = ['history'] as const;

const enc = encodeURIComponent;

export interface HistoryRange {
  fromMs: number;
  toMs: number;
}

/** Downsampled signal series for one device+metric. `bucket` omitted ⇒ server auto-picks by range. */
export function useSignalHistory(mac: string, metric: string, range: HistoryRange, bucket?: HistoryBucket) {
  const q = new URLSearchParams({ from: String(range.fromMs), to: String(range.toMs) });
  if (bucket) q.set('bucket', bucket);
  return useQuery({
    queryKey: [...KEY, 'signal', mac, metric, range.fromMs, range.toMs, bucket ?? 'auto'],
    queryFn: () => api.get<SignalHistory>(`${BASE}/${enc(mac)}/signals/${enc(metric)}?${q}`),
    // A range change is a new key — hold the previous window on screen (the page
    // marks it as refreshing) rather than dropping back to a first-load spinner.
    placeholderData: keepPreviousData,
    enabled: Boolean(mac && metric && range.toMs > range.fromMs),
  });
}

export function useActuatorStateHistory(mac: string, range: HistoryRange, actuator?: string) {
  const q = new URLSearchParams({ from: String(range.fromMs), to: String(range.toMs) });
  if (actuator) q.set('actuator', actuator);
  return useQuery({
    queryKey: [...KEY, 'state', mac, range.fromMs, range.toMs, actuator ?? 'all'],
    queryFn: () => api.get<EventPage<StateEventHist>>(`${BASE}/${enc(mac)}/actuator-states?${q}`),
    // A range change is a new key — hold the previous window on screen (the page
    // marks it as refreshing) rather than dropping back to a first-load spinner.
    placeholderData: keepPreviousData,
    enabled: Boolean(mac && range.toMs > range.fromMs),
  });
}

export function useHealthHistory(mac: string, range: HistoryRange) {
  const q = new URLSearchParams({ from: String(range.fromMs), to: String(range.toMs) });
  return useQuery({
    queryKey: [...KEY, 'health', mac, range.fromMs, range.toMs],
    queryFn: () => api.get<EventPage<HealthEventHist>>(`${BASE}/${enc(mac)}/health?${q}`),
    // A range change is a new key — hold the previous window on screen (the page
    // marks it as refreshing) rather than dropping back to a first-load spinner.
    placeholderData: keepPreviousData,
    enabled: Boolean(mac && range.toMs > range.fromMs),
  });
}

export function useErrorHistory(mac: string, range: HistoryRange) {
  const q = new URLSearchParams({ from: String(range.fromMs), to: String(range.toMs) });
  return useQuery({
    queryKey: [...KEY, 'error', mac, range.fromMs, range.toMs],
    queryFn: () => api.get<EventPage<ErrorEventHist>>(`${BASE}/${enc(mac)}/errors?${q}`),
    // A range change is a new key — hold the previous window on screen (the page
    // marks it as refreshing) rather than dropping back to a first-load spinner.
    placeholderData: keepPreviousData,
    enabled: Boolean(mac && range.toMs > range.fromMs),
  });
}
